import { useState, useRef, useEffect } from 'react';
import { Search, ChevronRight, ChevronLeft } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import { useUserStore } from '../../zustand/UserStore';
import { useHierarchicalUsersStore } from '../../zustand/HierarchicalUsersStore';
import type { User } from '../../types/dashboard';

interface UserSelectorProps {
  selectedUser: User | null;
  onUserSelect: (user: User | null) => void;
}

export default function UserSelector({ selectedUser, onUserSelect }: UserSelectorProps) {
  const isDarkMode = useTheme((state) => state.theme);
  const user = useUserStore((state) => state.user);
  const { users, isLoading, fetchUsers } = useHierarchicalUsersStore();
  const [userSearch, setUserSearch] = useState('');
  const userScrollRef = useRef<HTMLDivElement>(null);

  // Fetch users data using shared store
  useEffect(() => {
    if (user) {
      fetchUsers();
    }
  }, [user, fetchUsers]);

  const getVisibleUsers = () => {
    if (!user || !Array.isArray(users)) return [];

    return users.filter(user =>
      user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.role.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.designation.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearch.toLowerCase())
    );
  };

  const visibleUsers = getVisibleUsers();

  const scrollUsers = (direction: 'left' | 'right') => {
    if (userScrollRef.current) {
      const scrollAmount = 200;
      userScrollRef.current.scrollLeft += direction === 'left' ? -scrollAmount : scrollAmount;
    }
  };

  if (isLoading) {
    return (
      <div className={`rounded-xl p-6 mb-10 ${
        isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white border border-gray-200 shadow-sm'
      }`}>
        <div className="animate-pulse">
          <div className={`h-6 rounded mb-6 ${
            isDarkMode ? 'bg-[#1a2424]' : 'bg-gray-200'
          }`} style={{ width: '200px' }}></div>
          <div className="flex gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`w-40 h-32 rounded-2xl ${
                isDarkMode ? 'bg-[#1a2424]' : 'bg-gray-200'
              }`}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (visibleUsers.length === 0 && userSearch.trim() === '') {
    return (
      <div className={`text-center py-12 ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        <p className="text-lg mb-4">Welcome! Access your call analytics through the navigation menu.</p>
      </div>
    );
  }

  // Show "No results found" when search returns empty results
  if (visibleUsers.length === 0 && userSearch.trim() !== '') {
    return (
      <div className="mb-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-2xl font-bold ${
            isDarkMode ? 'text-white' : 'text-[#053333]'
          }`}>
            Select User
          </h2>

          {/* Search Bar */}
          <div className="relative w-80">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className={`w-full pl-12 pr-4 py-2.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] ${
                isDarkMode 
                  ? 'bg-[#0a1414] text-white border-2 border-[#1a2424] placeholder-gray-500' 
                  : 'bg-white text-[#053333] border-2 border-gray-200 placeholder-gray-400'
              }`}
            />
          </div>
        </div>

        {/* No results message */}
        <div className={`text-center py-12 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          <Search className={`w-12 h-12 mx-auto mb-4 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`} />
          <p className="text-lg mb-2">No users found</p>
          <p className="text-sm">
            Try adjusting your search criteria for "{userSearch}"
          </p>
          <button
            onClick={() => setUserSearch('')}
            className={`mt-4 px-4 py-2 rounded-lg transition-all duration-300 ${
              isDarkMode 
                ? 'bg-[#1a9f6b] hover:bg-[#148856] text-white' 
                : 'bg-[#1a9f6b] hover:bg-[#148856] text-white'
            }`}
          >
            Clear Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-2xl font-bold ${
          isDarkMode ? 'text-white' : 'text-[#053333]'
        }`}>
          Select User
        </h2>

        {/* Search Bar */}
        <div className="relative w-80">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`} />
          <input
            type="text"
            placeholder="Search users..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            className={`w-full pl-12 pr-4 py-2.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] ${
              isDarkMode 
                ? 'bg-[#0a1414] text-white border-2 border-[#1a2424] placeholder-gray-500' 
                : 'bg-white text-[#053333] border-2 border-gray-200 placeholder-gray-400'
            }`}
          />
        </div>
      </div>

      <div className="relative w-full">
        <button
          onClick={() => scrollUsers('left')}
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full transition-all duration-300 hover:scale-110 ${
            isDarkMode 
              ? 'bg-[#0a1414] text-white border border-[#1a2424] hover:bg-[#0f1919]' 
              : 'bg-white text-[#053333] border border-gray-200 hover:bg-gray-50 shadow-md'
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          onClick={() => scrollUsers('right')}
          className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full transition-all duration-300 hover:scale-110 ${
            isDarkMode 
              ? 'bg-[#0a1414] text-white border border-[#1a2424] hover:bg-[#0f1919]' 
              : 'bg-white text-[#053333] border border-gray-200 hover:bg-gray-50 shadow-md'
          }`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <div
          ref={userScrollRef}
          className="flex gap-4 overflow-x-scroll px-12 py-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {visibleUsers.map((user) => (
            <button
              key={user.id}
              onClick={() => onUserSelect(user)}
              className={`relative flex-shrink-0 w-40 p-6 rounded-2xl transition-all duration-300 transform hover:scale-105 border-2 ${
                selectedUser?.id === user.id 
                  ? 'bg-[#1a9f6b] border-[#1a9f6b] scale-105 shadow-xl shadow-[#1a9f6b]/30' 
                  : isDarkMode 
                    ? 'bg-[#0a1414] border-[#1a2424] hover:border-[#1a9f6b] hover:shadow-lg' 
                    : 'bg-white border-gray-200 hover:border-[#1a9f6b] hover:shadow-lg shadow-sm'
              }`}
            >
              {selectedUser?.id === user.id && (
                <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-white text-[#1a9f6b]' : 'bg-[#1a9f6b] text-white'
                }`}>
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              <div
                className={`w-16 h-16 mx-auto mb-3 rounded-full border-3 flex items-center justify-center text-lg font-bold ${
                  selectedUser?.id === user.id 
                    ? 'border-white bg-white text-[#1a9f6b]' 
                    : 'border-transparent bg-[#1a9f6b] text-white'
                }`}
              >
                {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </div>
              <p className={`font-semibold text-sm ${
                selectedUser?.id === user.id 
                  ? 'text-white' 
                  : isDarkMode ? 'text-white' : 'text-[#053333]'
              }`}>
                {user.name}
              </p>
              <p className={`text-xs mt-1 ${
                selectedUser?.id === user.id 
                  ? 'text-white/80' 
                  : isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {user.designation || user.role}
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
